import type { ContentSeedLink, ContentSeedLinkType } from '@/types/content';

// SPEC-021: Shows any practice seeds cross-linked from the content item.
// v1 just names them; a follow-up phase can turn each row into a CTA that
// creates a self-assignment (via a shared /assignments POST).

const LABELS: Record<ContentSeedLinkType, string> = {
  prerequisite: 'Prepare first',
  companion: 'Practice alongside',
  followup: 'Try after',
};

interface RelatedSeedsProps {
  links: ContentSeedLink[];
}

export function RelatedSeeds({ links }: RelatedSeedsProps) {
  if (links.length === 0) return null;

  const grouped = links.reduce<Record<ContentSeedLinkType, ContentSeedLink[]>>(
    (acc, l) => {
      (acc[l.link_type] ??= []).push(l);
      return acc;
    },
    { prerequisite: [], companion: [], followup: [] },
  );

  return (
    <section className="space-y-2 px-4">
      <h2 className="text-sm font-semibold text-[var(--color-fg)]">Related practices</h2>
      <div className="space-y-2">
        {(Object.keys(grouped) as ContentSeedLinkType[]).map((type) => {
          if (grouped[type].length === 0) return null;
          return (
            <div key={type} className="rounded-xl bg-[var(--color-secondary-bg)] p-3">
              <p className="mb-1 text-xs font-medium text-[var(--color-muted)]">
                {LABELS[type]}
              </p>
              <ul className="space-y-1">
                {grouped[type].map((l) => (
                  <li key={`${l.seed_id}-${l.link_type}`} className="text-sm">
                    <code className="text-xs text-[var(--color-muted)]">
                      {l.seed_id.slice(0, 8)}…
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
